import type { Settings as LayoutSettings } from '@ant-design/pro-components';
import { SettingDrawer } from '@ant-design/pro-components';
import type { RequestConfig, RunTimeLayoutConfig } from '@umijs/max';
import { history, Link } from '@umijs/max';
import { ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import React from 'react';
import {
  applyResolvedTheme,
  configureUiThemes,
  getUiTheme,
  isVizUiTheme,
} from '@yunnao/ui-themes';
import '@yunnao/ui-themes/styles.css';

// Initialize dayjs plugins globally
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
/** 费用发生日等业务日历按中国时区，避免浏览器非 CN 时跨日 */
dayjs.tz.setDefault('Asia/Shanghai');

import {
  AvatarDropdown,
  ErrorBoundary,
  Footer,
  OfflineBanner,
} from '@/components';
import WecomSensitiveAuthBootstrap from '@/components/WecomSensitiveAuthBootstrap';
import AccessHeartbeat from '@/components/AccessHeartbeat';
import { ThemeConfigButton } from '@/components/RightContent/ThemeConfigButton';
import { currentUser as queryCurrentUser } from '@/services/ant-design-pro/api';
import { clearToken, getToken } from '@/services/auth/wecom';
import { redirectToLogin } from '@/utils/authSession';
import defaultSettings from '../config/defaultSettings';
import { errorConfig } from './requestErrorConfig';

configureUiThemes({
  productId: 'deroad-erp',
  storageKey: 'deroad_erp_ui_theme_id',
  defaultThemeId: 'classic',
  /** 开放主题商城，用户可在顶栏换肤 / 拼装 */
  enableMall: true,
  title:
    typeof defaultSettings.title === 'string'
      ? defaultSettings.title
      : '德路机械数智营销系统',
  logo:
    typeof defaultSettings.logo === 'string'
      ? defaultSettings.logo
      : '/logo.svg',
  layout: {
    navTheme: defaultSettings.navTheme,
    colorPrimary: defaultSettings.colorPrimary,
    layout: defaultSettings.layout,
    contentWidth: defaultSettings.contentWidth,
    fixedHeader: defaultSettings.fixedHeader,
    fixSiderbar: defaultSettings.fixSiderbar,
    colorWeak: defaultSettings.colorWeak,
    splitMenus: defaultSettings.splitMenus,
  },
});

const isDev = process.env.NODE_ENV === 'development';
const loginPath = '/user/login';

const isAuthFreePath = (pathname: string) =>
  pathname === loginPath ||
  pathname.startsWith('/user/') ||
  pathname.startsWith('/public/');

/**
 * @see https://umijs.org/docs/api/runtime-config#getinitialstate
 * */
export async function getInitialState(): Promise<{
  settings?: Partial<LayoutSettings>;
  currentUser?: API.CurrentUser;
  loading?: boolean;
  fetchUserInfo?: () => Promise<API.CurrentUser | undefined>;
  settingDrawerOpen?: boolean;
  uiThemeId?: string;
  siderCollapsed?: boolean;
}> {
  const { theme, settings } =
    typeof window !== 'undefined'
      ? applyResolvedTheme()
      : { theme: getUiTheme('classic'), settings: defaultSettings };
  const uiThemeId = theme.id;
  const siderCollapsed = isVizUiTheme(uiThemeId);

  const fetchUserInfo = async () => {
    try {
      const msg = await queryCurrentUser({
        skipErrorHandler: true,
      });
      const user = (msg as any)?.data ?? msg;
      if (user && (user.userid || user.name)) {
        return user as API.CurrentUser;
      }
      return undefined;
    } catch (e: any) {
      const status = e?.response?.status;
      if (status === 401) {
        clearToken();
      }
      return undefined;
    }
  };

  // 如果不是登录/公开页，执行
  const { location } = history;
  if (!isAuthFreePath(location.pathname)) {
    const currentUser = await fetchUserInfo();
    if (!currentUser) {
      if (getToken()) {
        // 有 token 但拉不到用户（多为后端暂不可用），保留 token 避免误踢
        return {
          fetchUserInfo,
          settings: settings as Partial<LayoutSettings>,
          settingDrawerOpen: false,
          uiThemeId,
          siderCollapsed,
        };
      }
      redirectToLogin();
      return {
        fetchUserInfo,
        settings: settings as Partial<LayoutSettings>,
        settingDrawerOpen: false,
        uiThemeId,
        siderCollapsed,
      };
    }
    return {
      fetchUserInfo,
      currentUser,
      settings: settings as Partial<LayoutSettings>,
      settingDrawerOpen: false,
      uiThemeId,
      siderCollapsed,
    };
  }
  return {
    fetchUserInfo,
    settings: settings as Partial<LayoutSettings>,
    settingDrawerOpen: false,
    uiThemeId,
    siderCollapsed,
  };
}

// ProLayout 支持的api https://procomponents.ant.design/components/layout
export const layout: RunTimeLayoutConfig = ({
  initialState,
  setInitialState,
}) => {
  const uiThemeId = initialState?.uiThemeId || 'classic';
  const uiTheme = getUiTheme(uiThemeId);
  const isViz = isVizUiTheme(uiThemeId);

  return {
    menuItemRender: (item, dom) => {
      if (item.path) {
        return (
          <Link to={item.path} prefetch>
            {dom}
          </Link>
        );
      }
      return dom;
    },
    actionsRender: () => [<ThemeConfigButton key="theme" />],
    avatarProps: {
      src: initialState?.currentUser?.avatar,
      title: initialState?.currentUser?.name || 'ERP 用户',
      render: (_, avatarChildren) => (
        <AvatarDropdown>{avatarChildren}</AvatarDropdown>
      ),
    },
    footerRender: () => <Footer />,
    onPageChange: () => {
      const { location } = history;
      if (isAuthFreePath(location.pathname)) {
        return;
      }
      // 已登录
      if (initialState?.currentUser) return;

      if (getToken()) {
        clearToken();
      }
      history.replace(
        `${loginPath}?redirect=${encodeURIComponent(location.pathname + location.search + location.hash)}`,
      );
    },
    bgLayoutImgList: [],
    links: [
      <a key="deroad" href="https://deroad.top" target="_blank" rel="noreferrer">
        Deroad（德路）
      </a>,
    ],
    ErrorBoundary,
    menuHeaderRender: undefined,
    childrenRender: (children) => {
      const hideSettingDrawer =
        typeof window !== 'undefined' && window.innerWidth < 768;
      return (
        <ConfigProvider theme={uiTheme.antd}>
          <WecomSensitiveAuthBootstrap />
          <AccessHeartbeat />
          {children}
          {!hideSettingDrawer && isDev && (
            <SettingDrawer
              disableUrlParams
              enableDarkTheme
              collapse={initialState?.settingDrawerOpen}
              onCollapseChange={(open) => {
                setInitialState((s) => ({
                  ...s,
                  settingDrawerOpen: open,
                }));
              }}
              settings={initialState?.settings}
              onSettingChange={(settings) => {
                setInitialState((s) => ({
                  ...s,
                  settings,
                }));
              }}
            />
          )}
        </ConfigProvider>
      );
    },
    className: uiTheme.dark ? 'ui-layout-dark' : 'ui-layout-light',
    ...initialState?.settings,
    ...uiTheme.layout,
    title: defaultSettings.title,
    logo: uiTheme.dark ? '/logo-on-dark.svg' : defaultSettings.logo,
    // 可视化深色主题：关闭断点自动展开，默认收起侧栏
    breakpoint: isViz ? false : undefined,
    collapsed: initialState?.siderCollapsed ?? isViz,
    onCollapse: (collapsed) => {
      void setInitialState((state) => ({
        ...state,
        siderCollapsed: collapsed,
      }));
    },
  };
};

/**
 * @name request 配置，可以配置错误处理
 * 它基于 axios 提供了一套统一的网络请求和错误处理方案。
 * @doc https://umijs.org/docs/max/request#配置
 *
 * 生产环境走同域相对路径 `/api/*`，由 Nginx 反代到 Nest；
 * 勿再指向 Ant Design Pro 演示 API（会导致跨域 CORS）。
 * 如需直连其它域名，构建时设置 `API_BASE_URL`。
 */
export const request: RequestConfig = {
  baseURL: process.env.API_BASE_URL || '',
  ...errorConfig,
};

export function rootContainer(container: React.ReactNode) {
  return (
    <>
      <OfflineBanner />
      <ErrorBoundary>{container}</ErrorBoundary>
    </>
  );
}
